// src/pages/CookiePolicy.jsx
import React from 'react';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
    return (
        <div className="container mx-auto px-4 py-12 max-w-4xl">
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-cookie-bite text-white text-2xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-3">
                    Cookie Policy
                </h1>
                <p className="text-gray-600">How we use cookies on GradPath</p>
                <p className="text-sm text-gray-500 mt-2">Last updated: June 27, 2026</p>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 space-y-6">
                <section className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                    <p className="text-yellow-800 text-sm">
                        <i className="fas fa-info-circle mr-2"></i>
                        This Cookie Policy explains how GradPath uses cookies and similar technologies.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-question-circle text-gradpath-green text-lg"></i>
                        What Are Cookies?
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently and to provide information to the website owners.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-list text-gradpath-green text-lg"></i>
                        Types of Cookies We Use
                    </h2>
                    <div className="space-y-4">
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">1. Essential Cookies</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                These cookies are necessary for the Platform to function properly. They enable core functionality such as security, network management, and accessibility.
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Examples: Session cookies, authentication cookies</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">2. Preference Cookies</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                These cookies remember your preferences and settings to enhance your experience, such as remembering your login status and language preferences.
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Examples: Theme preferences, language settings</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">3. Analytics Cookies</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                These cookies help us understand how visitors interact with our Platform by collecting and reporting information anonymously.
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Examples: Google Analytics cookies</p>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-bold text-gradpath-dark">4. Marketing Cookies</h3>
                            <p className="text-sm text-gray-600 mt-1">
                                These cookies are used to track visitors across websites to display relevant advertisements and measure the effectiveness of marketing campaigns.
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Examples: Advertising cookies</p>
                        </div>
                    </div>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-clock text-gradpath-green text-lg"></i>
                        How Long Do Cookies Last?
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        Cookies may be either "session" cookies (deleted when you close your browser) or "persistent" cookies (remain on your device until they expire or you delete them). The duration varies depending on the cookie type and purpose.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-cog text-gradpath-green text-lg"></i>
                        Managing Cookies
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        You can manage or delete cookies at any time through your browser settings. You can:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Delete all cookies stored on your device</li>
                        <li>Block certain types of cookies</li>
                        <li>Set your browser to notify you before accepting cookies</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                        <strong>Note:</strong> Blocking essential cookies may affect the functionality of our Platform.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-edit text-gradpath-green text-lg"></i>
                        Changes to This Policy
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We may update this Cookie Policy from time to time. Changes become effective immediately upon posting. Your continued use of the Platform after changes constitutes acceptance of the updated policy.
                    </p>
                </section>

                <section className="bg-gray-50 p-4 rounded-lg">
                    <h2 className="text-lg font-bold text-gradpath-dark mb-2 flex items-center gap-2">
                        <i className="fas fa-envelope text-gradpath-green"></i>
                        Contact Us
                    </h2>
                    <p className="text-gray-700 text-sm">
                        If you have questions about our Cookie Policy, please contact us at:
                        <a href="mailto:support@gradpath.co.za" className="text-gradpath-green hover:underline block mt-1">
                            support@gradpath.co.za
                        </a>
                    </p>
                </section>

                <div className="text-center pt-4">
                    <Link to="/" className="inline-flex items-center gap-2 text-gradpath-green hover:underline">
                        <i className="fas fa-arrow-left"></i>
                        Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CookiePolicy;